import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  Paper,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
  InputBase,
  Select,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Zoom,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCompactIcon from '@mui/icons-material/ViewComfy';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CheckIcon from '@mui/icons-material/Check';
import {
  getProducts,
  getMedias,
  getCategoryTree,
  purgeAllLegacySKUs,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import { ExtensionSlot } from '../../core/public/ExtensionSlot';
import { isImageMimeOrExtension } from '../../medias/public/mediaMime';
import { formatListSkuMoneyRange } from '../private/productVariantUtils';
import { flattenCategoryTree, normalizeCategoryTreeNode } from '../../categories/public/categoryUtils';
import { mutedSurfaceBg } from '../../../theme/surfaceSx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 24;
// Distância (px) do fim da lista em que a próxima página começa a carregar.
const LOAD_AHEAD_PX = 600;
const DENSITY_STORAGE_KEY = 'eagle.products.gallery_density';
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const STATUS_FILTERS = [
  { value: 'ALL', labelKey: 'products.filter_all' },
  { value: 'ACTIVE', labelKey: 'products.active' },
  { value: 'INACTIVE', labelKey: 'products.inactive' },
];

const SORT_OPTIONS = [
  { value: 'updated_at:desc', labelKey: 'products.gallery_sort_updated_desc' },
  { value: 'updated_at:asc', labelKey: 'products.gallery_sort_updated_asc' },
  { value: 'name:asc', labelKey: 'products.gallery_sort_name_asc' },
  { value: 'name:desc', labelKey: 'products.gallery_sort_name_desc' },
  { value: 'code:asc', labelKey: 'products.gallery_sort_code_asc' },
  { value: 'sku_count:desc', labelKey: 'products.gallery_sort_variations_desc' },
];

const GRID_COLUMNS = {
  comfortable: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(220px, 1fr))' },
  compact: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(160px, 1fr))' },
};

// Cor da paleta usada por cada tom de selo de estoque (null = neutro).
const STOCK_TONE_PALETTE = {
  ok: 'success',
  out: 'error',
  neutral: null,
};

const CONTROL_SX = {
  height: 40,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  fontSize: '0.875rem',
  transition: 'border-color .15s, box-shadow .15s',
  '&:hover': { borderColor: 'primary.main' },
  '&.Mui-focused': {
    borderColor: 'primary.main',
    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
};

const SELECT_SX = {
  ...CONTROL_SX,
  minWidth: { xs: 0, sm: 200 },
  flex: { xs: '1 1 140px', sm: '0 0 auto' },
  '& .MuiOutlinedInput-notchedOutline': { border: 0 },
  '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center', height: '100%' },
};

const readStoredDensity = () => {
  try {
    return localStorage.getItem(DENSITY_STORAGE_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
};

const getScrollParent = (node) => {
  let el = node?.parentElement;
  while (el && el !== document.body) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return window;
};

const buildProductImagesMap = (productsList, allMedias) => {
  const imagesMap = {};
  productsList.forEach((product) => {
    const productMedias = allMedias
      .map((media) => {
        if (!media.metadata?.length) return null;
        const matchingMeta = media.metadata.find((meta) => meta.key === 'product_image');
        if (!matchingMeta) return null;
        const metaValue = matchingMeta.value;
        const isObject = typeof metaValue === 'object' && metaValue !== null;
        if (!isObject) {
          if (metaValue !== product.id) return null;
          return { ...media, order: 0 };
        }
        if (metaValue.id !== product.id) return null;
        return { ...media, order: metaValue?.order || 0 };
      })
      .filter(Boolean)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const images = productMedias.filter((m) => isImageMimeOrExtension(m.mime_type, m.filename));
    if (images.length) {
      imagesMap[product.id] = { url: `${API_URL}${images[0].file_path}`, count: images.length };
    }
  });
  return imagesMap;
};

const ProductsPage = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(null);
  const [productImages, setProductImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilterId, setCategoryFilterId] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState(SORT_OPTIONS[0].value);
  const [density, setDensity] = useState(readStoredDensity);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const rootRef = useRef(null);
  // Estado de paginação em ref: o observer e as respostas assíncronas leem sempre o valor atual.
  const pagingRef = useRef({ page: 0, loading: false, hasMore: true, generation: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [confirmPurgeAllLegacy, setConfirmPurgeAllLegacy] = useState(false);
  const [purgingAllLegacy, setPurgingAllLegacy] = useState(false);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCategoryTree();
        const tree = (res.data?.tree ?? res.data?.Tree ?? []).map(normalizeCategoryTreeNode).filter(Boolean);
        setCategoryOptions(flattenCategoryTree(tree));
      } catch {
        setCategoryOptions([]);
      }
    })();
  }, []);

  // "/" foca a busca.
  useEffect(() => {
    const onKeyDown = (e) => {
      const active = document.activeElement;
      const typing = active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA' || active?.isContentEditable;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const loadNextPage = useCallback(async () => {
    const paging = pagingRef.current;
    if (paging.loading || !paging.hasMore) return;
    paging.loading = true;
    setLoading(true);
    const { generation } = paging;
    const nextPage = paging.page + 1;
    try {
      const [sortField, sortOrder] = sortValue.split(':');
      const query = { sort: sortField, order: sortOrder, page: nextPage, limit: PAGE_SIZE };
      if (debouncedSearch) query.q = debouncedSearch;
      if (statusFilter !== 'ALL') query.status = statusFilter;
      if (categoryFilterId) query.category_id = categoryFilterId;

      const [productsResponse, mediasResponse] = await Promise.all([getProducts(query), getMedias()]);
      // Filtros mudaram enquanto a requisição estava em andamento: descarta a resposta.
      if (generation !== pagingRef.current.generation) return;

      const pageProducts = productsResponse.data.products || [];
      const totalCount = productsResponse.data.total ?? pageProducts.length;
      const allMedias = mediasResponse.data.medias || [];

      paging.page = nextPage;
      paging.hasMore = pageProducts.length === PAGE_SIZE && nextPage * PAGE_SIZE < totalCount;
      setHasMore(paging.hasMore);
      setTotal(totalCount);
      setProductImages((prev) => ({ ...prev, ...buildProductImagesMap(pageProducts, allMedias) }));
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...pageProducts.filter((p) => !seen.has(p.id))];
      });
    } catch (error) {
      if (generation !== pagingRef.current.generation) return;
      paging.hasMore = false;
      setHasMore(false);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      if (generation === pagingRef.current.generation) {
        paging.loading = false;
        setLoading(false);
      }
    }
  }, [debouncedSearch, statusFilter, categoryFilterId, sortValue]);

  const resetAndLoad = useCallback(() => {
    pagingRef.current = {
      page: 0,
      loading: false,
      hasMore: true,
      generation: pagingRef.current.generation + 1,
    };
    setProducts([]);
    setProductImages({});
    setTotal(null);
    setHasMore(true);
    loadNextPage();
  }, [loadNextPage]);

  // Qualquer mudança de filtro/ordenação recria loadNextPage e recomeça a lista.
  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: `${LOAD_AHEAD_PX}px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadNextPage]);

  // Em telas grandes a primeira página pode não preencher a área visível; o observer
  // não dispara de novo se o sentinela continuar visível, então checamos após cada carga.
  useEffect(() => {
    if (loading || !hasMore || products.length === 0) return;
    const sentinel = sentinelRef.current;
    if (sentinel && sentinel.getBoundingClientRect().top < window.innerHeight + LOAD_AHEAD_PX) {
      loadNextPage();
    }
  }, [loading, hasMore, products.length, loadNextPage]);

  useEffect(() => {
    const scroller = getScrollParent(rootRef.current);
    const onScroll = () => {
      const top = scroller === window ? window.scrollY : scroller.scrollTop;
      setShowBackToTop(top > 800);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    getScrollParent(rootRef.current).scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDensityChange = (_, value) => {
    if (!value) return;
    setDensity(value);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch {
      // Preferência só da sessão quando o storage está indisponível.
    }
  };

  const getSkuCount = (product) => product.sku_count ?? product.skuCount ?? 0;

  const getProductDisplayName = (product) => {
    const name = (product?.name ?? '').trim();
    return name || translate('products.unnamed_label');
  };

  const getMainCategoryName = (product) => {
    const main = product.main_category ?? product.mainCategory;
    return main?.name || '';
  };

  const getStockBadge = (product) => {
    if (!getSkuCount(product)) return { label: translate('products.gallery_no_variants'), tone: 'neutral' };
    if (product.all_made_to_order ?? product.allMadeToOrder) {
      return { label: translate('products.made_to_order_label'), tone: 'neutral' };
    }
    const stockTotal = Number(product.stock_total ?? product.stockTotal ?? 0);
    if (!Number.isFinite(stockTotal)) return null;
    if (stockTotal <= 0) return { label: translate('products.gallery_out_of_stock'), tone: 'out' };
    const qty = stockTotal % 1 === 0 ? stockTotal : Math.round(stockTotal * 100) / 100;
    return { label: translate('products.gallery_stock_units').replace('{qty}', qty.toLocaleString()), tone: 'ok' };
  };

  const formatVariationCount = (count) =>
    count === 1
      ? translate('products.gallery_variation_count_one')
      : translate('products.gallery_variations_count').replace('{count}', String(count));

  const productSlug = (product) => encodeURIComponent(product.code || product.id);
  const productEditPath = (product) => `/products/${productSlug(product)}/edit`;
  const productViewPath = (product) => `/products/${productSlug(product)}/view`;

  const handlePurgeAllLegacy = async () => {
    setPurgingAllLegacy(true);
    try {
      const res = await purgeAllLegacySKUs();
      const data = res.data ?? {};
      setConfirmPurgeAllLegacy(false);
      resetAndLoad();
      const msg = translate('products.purge_all_unused_success')
        .replace('{deleted}', String(data.deleted ?? 0))
        .replace('{products}', String(data.products_processed ?? 0))
        .replace('{retained}', String(data.retained ?? 0));
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setPurgingAllLegacy(false);
    }
  };

  const compact = density === 'compact';

  const renderStockPill = (badge, size = 'small') => {
    if (!badge) return null;
    const paletteKey = STOCK_TONE_PALETTE[badge.tone];
    return (
      <Box
        component="span"
        sx={(theme) => {
          const dark = theme.palette.mode === 'dark';
          const main = paletteKey ? theme.palette[paletteKey].main : null;
          return {
            display: 'inline-block',
            px: 0.9,
            py: size === 'small' ? '2px' : '4px',
            borderRadius: 1,
            fontSize: size === 'small' ? '0.6875rem' : '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            color: main ? theme.palette[paletteKey][dark ? 'light' : 'dark'] : theme.palette.text.secondary,
            bgcolor: main ? alpha(main, dark ? 0.18 : 0.12) : mutedSurfaceBg(theme),
          };
        }}
      >
        {badge.label}
      </Box>
    );
  };

  const renderPhoto = (product, { hero = false } = {}) => {
    const image = productImages[product.id];
    if (image) {
      return (
        <Box
          component="img"
          className="product-card-photo"
          src={image.url}
          alt=""
          loading="lazy"
          sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', transition: 'transform .3s ease' }}
        />
      );
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          color: 'text.disabled',
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: hero ? 56 : compact ? 32 : 40 }} />
        {(hero || !compact) && (
          <Typography variant="caption" color="text.disabled">
            {translate('products.gallery_no_photo')}
          </Typography>
        )}
      </Box>
    );
  };

  const overlayPillSx = {
    position: 'absolute',
    px: 1,
    py: '3px',
    borderRadius: 999,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.4,
  };

  const renderProductCard = (product) => {
    const image = productImages[product.id];
    const priceText = formatListSkuMoneyRange(product, 'price');
    const inactive = product.status !== 'ACTIVE';
    const name = getProductDisplayName(product);

    return (
      <Paper
        key={product.id}
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 1px 2px rgba(0,0,0,.4)'
              : '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.08)',
          transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
          animation: 'productCardIn .3s ease both',
          '@keyframes productCardIn': {
            from: { opacity: 0, transform: 'translateY(6px)' },
            to: { opacity: 1, transform: 'none' },
          },
          '&:hover, &:focus-within': {
            transform: 'translateY(-2px)',
            borderColor: 'primary.main',
            boxShadow:
              theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,.5)' : '0 8px 24px rgba(16,24,40,.12)',
          },
          '&:hover .product-card-photo': { transform: 'scale(1.04)' },
          '&:hover .product-card-quick, &:focus-within .product-card-quick': { opacity: 1, transform: 'none' },
          '&:hover .product-card-count': { opacity: 0 },
        })}
      >
        <ButtonBase
          component={RouterLink}
          to={productViewPath(product)}
          aria-label={name}
          sx={{ position: 'absolute', inset: 0, zIndex: 1, borderRadius: 3 }}
        />
        <Box sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: mutedSurfaceBg, overflow: 'hidden' }}>
          {renderPhoto(product)}
          {inactive && (
            <Box
              sx={{
                ...overlayPillSx,
                top: 8,
                left: 8,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                boxShadow: 1,
              }}
            >
              {translate('products.inactive')}
            </Box>
          )}
          {image?.count > 1 && !compact && (
            <Box
              className="product-card-count"
              sx={{ ...overlayPillSx, bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', transition: 'opacity .15s' }}
            >
              {image.count}
            </Box>
          )}
          <Box
            className="product-card-quick"
            sx={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 8,
              zIndex: 2,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              gap: 0.75,
              opacity: 0,
              transform: 'translateY(4px)',
              transition: 'opacity .15s, transform .15s',
            }}
          >
            {[
              { label: translate('common.edit'), onClick: () => navigate(productEditPath(product)) },
            ].map((action) => (
              <ButtonBase
                key={action.label}
                onClick={action.onClick}
                sx={{
                  px: 1.25,
                  py: 0.6,
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  boxShadow: 2,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {action.label}
              </ButtonBase>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            p: compact ? '8px 10px 10px' : '10px 12px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? 0.4 : 0.6,
            flex: 1,
            minWidth: 0,
          }}
        >
          {!compact && (
            <Typography
              noWrap
              sx={{
                fontSize: '0.6875rem',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                minHeight: '1.3em',
              }}
            >
              {getMainCategoryName(product)}
            </Typography>
          )}
          <Typography
            title={name}
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.8125rem' : '0.875rem',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em',
            }}
          >
            {name}
          </Typography>
          <Typography noWrap sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem', color: 'text.secondary' }}>
            {product.code}
          </Typography>
          <Typography noWrap title={priceText} sx={{ fontWeight: 700, fontSize: compact ? '0.875rem' : '1rem' }}>
            {priceText || '—'}
          </Typography>
          <Box
            sx={{
              mt: 'auto',
              pt: 0.75,
              borderTop: '1px dashed',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: compact ? 'flex-end' : 'space-between',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {!compact && (
              <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {formatVariationCount(getSkuCount(product))}
              </Typography>
            )}
            {renderStockPill(getStockBadge(product))}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderSkeletonCard = (key) => (
    <Paper key={key} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Skeleton variant="rectangular" animation="wave" sx={{ aspectRatio: '1 / 1', height: 'auto' }} />
      <Box sx={{ p: compact ? 1 : 1.5 }}>
        <Skeleton animation="wave" width="40%" />
        <Skeleton animation="wave" width="90%" />
        <Skeleton animation="wave" width="55%" />
        <Skeleton animation="wave" width="35%" height={28} />
      </Box>
    </Paper>
  );

  const loadedCount = products.length;
  const statusLine = (() => {
    if (total == null || total === 0) return '';
    if (!hasMore) return translate('products.gallery_end').replace('{total}', total.toLocaleString());
    return translate('products.gallery_showing')
      .replace('{shown}', loadedCount.toLocaleString())
      .replace('{total}', total.toLocaleString());
  })();

  return (
    <Box ref={rootRef}>
      <Box
        component="header"
        sx={(theme) => ({
          // O sticky considera a margem: top = altura da AppBar − padding do <main>.
          position: 'sticky',
          top: { xs: 56 - 12, sm: 64 - 24 },
          zIndex: 5,
          mx: { xs: -1.5, sm: -3 },
          mt: { xs: -1.5, sm: -3 },
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1.5,
          mb: 2.5,
          bgcolor: alpha(theme.palette.background.default, 0.88),
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        })}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <InputBase
              inputRef={searchInputRef}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={translate('products.list_search_hint')}
              startAdornment={<SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />}
              endAdornment={
                searchInput ? (
                  <IconButton size="small" aria-label={translate('common.close')} onClick={() => setSearchInput('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Box
                    component="kbd"
                    sx={{
                      display: { xs: 'none', md: 'inline-block' },
                      px: 0.75,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.6875rem',
                      color: 'text.secondary',
                      fontFamily: 'inherit',
                    }}
                  >
                    /
                  </Box>
                )
              }
              sx={{ ...CONTROL_SX, flex: '1 1 280px', px: 1.25 }}
            />
            <Select
              value={categoryFilterId}
              onChange={(e) => setCategoryFilterId(e.target.value)}
              displayEmpty
              sx={SELECT_SX}
              inputProps={{ 'aria-label': translate('products.category_filter_label') }}
            >
              <MenuItem value="">{translate('products.filter_all_categories')}</MenuItem>
              {categoryOptions.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.label || cat.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={sortValue}
              onChange={(e) => setSortValue(e.target.value)}
              sx={SELECT_SX}
              inputProps={{ 'aria-label': translate('products.gallery_sort_label') }}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {translate(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={density}
              onChange={handleDensityChange}
              sx={{
                height: 40,
                bgcolor: 'background.paper',
                '& .MuiToggleButton-root': { px: 1.25, borderColor: 'divider' },
                '& .MuiToggleButton-root.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              <ToggleButton value="comfortable" aria-label={translate('products.gallery_view_comfortable')}>
                <Tooltip title={translate('products.gallery_view_comfortable')}>
                  <GridViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="compact" aria-label={translate('products.gallery_view_compact')}>
                <Tooltip title={translate('products.gallery_view_compact')}>
                  <ViewCompactIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: { md: 'auto' },
                // Botões vindos de extensões seguem a altura e o formato dos controles da barra.
                '& > .MuiButton-root': {
                  height: 40,
                  borderRadius: 2,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                },
                '& > .MuiButton-outlined': { borderColor: 'divider', color: 'text.primary', bgcolor: 'background.paper' },
                '& > .MuiButton-outlined:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              <ExtensionSlot slotId="products.list.actions" />
              <Button
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => navigate('/products/new')}
                sx={{ height: 40, borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {translate('common.new')} {translate('products.title')}
              </Button>
              <IconButton
                aria-label={translate('common.actions')}
                onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={() => setActionsMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setActionsMenuAnchor(null);
                    setConfirmPurgeAllLegacy(true);
                  }}
                >
                  {translate('products.purge_all_unused_variants')}
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((f) => {
              const selected = statusFilter === f.value;
              return (
                <ButtonBase
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  aria-pressed={selected}
                  sx={(theme) => ({
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 999,
                    fontSize: '0.8125rem',
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : 'background.paper',
                    color: selected ? 'primary.main' : 'text.primary',
                    fontWeight: selected ? 600 : 400,
                    transition: 'all .15s',
                    '&:hover': { borderColor: 'primary.main' },
                  })}
                >
                  {selected && <CheckIcon sx={{ fontSize: 14, mr: 0.5 }} />}
                  {translate(f.labelKey)}
                </ButtonBase>
              );
            })}
            {total != null && (
              <Typography sx={{ ml: 'auto', fontSize: '0.8125rem', color: 'text.secondary' }}>
                {translate('products.gallery_count').replace('{total}', total.toLocaleString())}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {total === 0 && !loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <ImageOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>{translate('products.no_products')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS[density], gap: compact ? 1.5 : 2 }}>
            {products.map(renderProductCard)}
            {loading &&
              Array.from({ length: loadedCount === 0 ? 12 : 6 }, (_, i) => renderSkeletonCard(`skeleton-${i}`))}
          </Box>
        )}

        <Box ref={sentinelRef} sx={{ height: 1 }} />
        <Box sx={{ pt: 3.5, pb: 1, textAlign: 'center', minHeight: 64 }}>
          {loading && loadedCount > 0 ? (
            <CircularProgress size={20} />
          ) : (
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{statusLine}</Typography>
          )}
        </Box>
      </Box>

      <Zoom in={showBackToTop}>
        <Fab
          size="small"
          aria-label={translate('products.gallery_back_to_top')}
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 88,
            bgcolor: 'background.paper',
            color: 'text.primary',
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>


      <ConfirmDialog
        open={confirmPurgeAllLegacy}
        title={translate('products.purge_all_unused_confirm_title')}
        message={translate('products.purge_all_unused_confirm_message')}
        confirmText={purgingAllLegacy ? translate('common.saving') : translate('products.purge_all_unused_variants')}
        cancelText={translate('common.cancel')}
        confirmColor="warning"
        onConfirm={() => {
          if (!purgingAllLegacy) void handlePurgeAllLegacy();
        }}
        onCancel={() => setConfirmPurgeAllLegacy(false)}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsPage;
